/**
 * TagManagerModal — create / rename / recolor / delete registry tags.
 * Popup card shell (design §2); deletion confirms and strips the tag from
 * every clinic that carries it.
 */

import { useState } from "react";
import { Plus, Tag, Trash, Check, PencilSimple } from "@phosphor-icons/react";
import ModalCloseButton from "../../../../components/ui/ModalCloseButton";
import ConfirmDialog from "../../../../components/ui/ConfirmDialog";
import { useToast } from "../../../../contexts/ToastContext";
import {
  useSaveNivClinicTagMutation,
  useDeleteNivClinicTagMutation,
} from "../../../../store/services/nivApi";
import { ColorSwatchPicker, UserTagChip } from "../shared/ClinicUserTags";

function TagRow({ tag, usage, onSave, onDelete, saving }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(tag.label);
  const [color, setColor] = useState(tag.color);

  const commit = async () => {
    const ok = await onSave({ id: tag.id, label, color });
    if (ok) setEditing(false);
  };

  if (!editing) {
    return (
      <div className="nivck2-tagmgr-row">
        <UserTagChip tag={tag} />
        <span className="nivck2-tagmgr-usage">{usage} קליניקות</span>
        <button
          type="button"
          className="nivck2-icon-btn"
          title="עריכה"
          onClick={() => {
            setLabel(tag.label);
            setColor(tag.color);
            setEditing(true);
          }}
        >
          <PencilSimple size={14} />
        </button>
        <button
          type="button"
          className="nivck2-icon-btn nivck2-icon-btn-danger"
          title="מחיקה"
          onClick={() => onDelete(tag)}
        >
          <Trash size={14} />
        </button>
      </div>
    );
  }

  return (
    <div className="nivck2-tagmgr-row is-editing">
      <input
        type="text"
        className="nivck2-input"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        maxLength={30}
        autoFocus
        onKeyDown={(e) => e.key === "Enter" && commit()}
      />
      <ColorSwatchPicker value={color} onChange={setColor} size={20} />
      <div className="nivck2-tagmgr-row-actions">
        <button type="button" className="nivck2-btn-ghost" onClick={() => setEditing(false)}>
          ביטול
        </button>
        <button
          type="button"
          className="nivck2-btn-primary"
          onClick={commit}
          disabled={saving || !label.trim()}
        >
          <Check size={13} weight="bold" />
          שמור
        </button>
      </div>
    </div>
  );
}

function TagManagerModal({ tags = [], usageByTag = {}, onClose }) {
  const { showSuccess, showError } = useToast();
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("mint");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saveTag, { isLoading: isSaving }] = useSaveNivClinicTagMutation();
  const [deleteTag, { isLoading: isDeleting }] = useDeleteNivClinicTagMutation();

  const handleSave = async ({ id, label, color }) => {
    try {
      await saveTag({ id, label, color }).unwrap();
      showSuccess(id ? "התגית עודכנה" : "התגית נוצרה");
      return true;
    } catch (error) {
      console.error(`[${id ? "CK-7307" : "CK-7306"}] Error saving clinic tag:`, {
        errorCode: id ? "CK-7307" : "CK-7306",
        errorMessage: error?.message,
        tagId: id || null,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בשמירת התגית");
      return false;
    }
  };

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    const ok = await handleSave({ label: newLabel, color: newColor });
    if (ok) setNewLabel("");
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTag({ tagId: deleteTarget.id }).unwrap();
      showSuccess("התגית נמחקה");
      setDeleteTarget(null);
    } catch (error) {
      console.error("[CK-7308] Error deleting clinic tag:", {
        errorCode: "CK-7308",
        errorMessage: error?.message,
        tagId: deleteTarget.id,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה במחיקת התגית");
    }
  };

  return (
    <div className="nivck2-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="nivck2-card nivut-scope" role="dialog" aria-modal="true" aria-label="ניהול תגיות">
        <div className="nivck2-card-head">
          <div className="nivck2-card-icon">
            <Tag size={20} weight="fill" />
          </div>
          <div className="nivck2-card-titles">
            <span className="nivck2-card-eyebrow">קליניקות</span>
            <h3 className="nivck2-card-title">ניהול תגיות</h3>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="nivck2-card-body">
          <div className="nivck2-tagmgr-new">
            <input
              type="text"
              className="nivck2-input"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="שם תגית חדשה…"
              maxLength={30}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
            <ColorSwatchPicker value={newColor} onChange={setNewColor} />
            <button
              type="button"
              className="nivck2-btn-primary"
              onClick={handleCreate}
              disabled={isSaving || !newLabel.trim()}
            >
              <Plus size={14} weight="bold" />
              הוסף תגית
            </button>
          </div>

          <div className="nivck2-tagmgr-list">
            {tags.length === 0 ? (
              <div className="nivck2-pop-empty">אין תגיות עדיין — צור את הראשונה למעלה</div>
            ) : (
              tags.map((tag) => (
                <TagRow
                  key={tag.id}
                  tag={tag}
                  usage={usageByTag[tag.id] || 0}
                  onSave={handleSave}
                  onDelete={setDeleteTarget}
                  saving={isSaving}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="מחיקת תגית"
        message={
          deleteTarget
            ? `התגית "${deleteTarget.label}" תוסר מ-${usageByTag[deleteTarget.id] || 0} קליניקות. להמשיך?`
            : ""
        }
        confirmText="מחק"
        type="danger"
        icon="delete"
        confirmDisabled={isDeleting}
      />
    </div>
  );
}

export default TagManagerModal;
